import type { SeoBreadcrumbItem } from '@xing-com/seo-breadcrumb';

import type { CompaniesRoute } from '../types';

const mapCrumb = (
  { path: defaultPath, name: defaultName }: CompaniesRoute,
  searchParams: { [key: string]: string },
  overrideNames?: { [route: string]: () => string }
): SeoBreadcrumbItem => {
  const haveSomeParam = Object.keys(searchParams).length > 0;

  const path = haveSomeParam
    ? Object.keys(searchParams).reduce(
        (path, param) => path.replace(`:${param}`, searchParams[param]),
        defaultPath
      )
    : defaultPath;

  const name =
    overrideNames && overrideNames[defaultPath]
      ? overrideNames[defaultPath]()
      : typeof defaultName === 'function'
        ? defaultName(searchParams)
        : defaultName;

  return {
    url: path,
    title: name ?? '',
  };
};

export const getCrumbs = (
  routes: CompaniesRoute[],
  currentPath: string,
  searchParams: { [key: string]: string },
  overrideNames?: { [route: string]: () => string }
) => {
  const pathSegments = currentPath.split('/').filter(Boolean);
  let accumulatedPath = '';
  const crumbs: SeoBreadcrumbItem[] = [];

  pathSegments.forEach((segment) => {
    accumulatedPath += `/${segment}`;

    // Find matching route for the current accumulated path
    const matchingRoute = routes.find(({ path: routePath }) => {
      const routeRegex = new RegExp(
        `^${routePath.replace(/:[^\s/]+/g, '([\\w.-]+)')}$`
      );
      return routeRegex.test(accumulatedPath);
    });

    if (matchingRoute) {
      const routeRegex = new RegExp(
        `^${matchingRoute.path.replace(/:[^\s/]+/g, '([\\w.-]+)')}$`
      );
      // Extract dynamic segments from the path
      const match = accumulatedPath.match(routeRegex);
      if (match) {
        const paramNames = (matchingRoute.path.match(/:[^\s/]+/g) || []).map(
          (param) => param.slice(1)
        );
        paramNames.forEach((paramName, index) => {
          searchParams[paramName] = match[index + 1];
        });
      }
      // Map the matched route to a breadcrumb
      const crumb = mapCrumb(matchingRoute, searchParams, overrideNames);
      crumbs.push(crumb);
    }
  });

  return crumbs;
};
